import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/native';
import PageParts from '@sp/ui/PageParts';
import { isWeb } from '@sp/ui/helpers/device';
import useModal from '@sp/ui/hooks/useModal';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Actions from './Actions';
import { groupActions, ActionShape } from './Actions/index.shared';

const Wrapper = styled.View`
  background: ${(props) => props.theme.COLORS.SECONDARY_BACKGROUND};
  flex: 1;
  height: 100%;
  ${isWeb ? 'user-select: text;' : ''}
`;

const Content = styled.View`
  flex: 1;
`;

const StatusBarOffset = styled.View`
  ${({ hasHeader, extraTopPadding }) => (hasHeader ? '' : `padding-top: ${extraTopPadding}px;`)}
  background: ${(props) => props.theme.COLORS.PRIMARY};
`;

const BasePage = ({
  menu,
  title,
  children,
  actions,
  actionInfo,
  backButton,
  hasBackButton,
  hasHeader,
  hasNotifications,
  customHeader,
  titleAnimation,
  testID,
  accessibilitySummary,
  animateTitle,
}) => {
  const { isModal, leftContent, onClose, hasOnCloseButton } = useModal();

  const insets = useSafeAreaInsets();
  const groupedActions = groupActions(actions);
  const hasCustomHeader = typeof customHeader !== 'undefined';
  const hasGlobalHeader = !hasCustomHeader && hasHeader && !isModal;
  const hasModalHeader = !hasCustomHeader && hasHeader && isModal;

  return (
    <PageParts>
      {({
        menu: globalMenu,
        Header: GlobalHeader,
        ModalHeader,
        BackButton: GlobalBackButton,
        notifications,
      }) => {
        const backButtonNode =
          hasBackButton && (typeof backButton !== 'undefined' ? backButton : <GlobalBackButton />);

        return (
          <Wrapper testID={testID}>
            <StatusBarOffset hasHeader={hasHeader} extraTopPadding={insets.top} />
            {isWeb && (typeof menu !== 'undefined' ? menu : globalMenu)}
            {hasCustomHeader && customHeader}
            {hasGlobalHeader && GlobalHeader && (
              <GlobalHeader
                title={title}
                animation={titleAnimation}
                actions={groupedActions.header}
                backButton={backButtonNode}
                accessibilitySummary={accessibilitySummary}
                animateTitle={animateTitle}
              />
            )}
            {hasModalHeader && ModalHeader && (
              <ModalHeader
                title={title}
                animation={titleAnimation}
                hasOnCloseButton={hasOnCloseButton}
                onClose={onClose}
                backButton={backButtonNode}
                leftContent={leftContent}
                animateTitle={animateTitle}
              />
            )}
            {hasNotifications && notifications}
            <Content>
              {children}
              {groupedActions.bottom.length > 0 && <Actions actions={groupedActions.bottom} />}
            </Content>
            {groupedActions.bottomSticky.length > 0 && (
              <Actions actions={groupedActions.bottomSticky} actionInfo={actionInfo} />
            )}
          </Wrapper>
        );
      }}
    </PageParts>
  );
};

BasePage.propTypes = {
  title: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  hasHeader: PropTypes.bool,
  customHeader: PropTypes.node,
  actions: PropTypes.arrayOf(ActionShape),
  actionInfo: PropTypes.string,
  backButton: PropTypes.node,
  hasBackButton: PropTypes.bool,
  menu: PropTypes.node,
  testID: PropTypes.string,
  titleAnimation: PropTypes.object,
  accessibilitySummary: PropTypes.string,
  hasNotifications: PropTypes.bool,
  animateTitle: PropTypes.bool,
};

BasePage.defaultProps = {
  title: undefined,
  children: undefined,
  actions: undefined,
  actionInfo: undefined,
  backButton: undefined,
  customHeader: undefined,
  hasHeader: true,
  hasBackButton: true,
  hasNotifications: true,
  menu: undefined,
  titleAnimation: undefined,
  testID: undefined,
  accessibilitySummary: undefined,
  animateTitle: false,
};

export default BasePage;
